import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { instanciaService, MetaTemplate } from '../../services/instanciaService';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2, Search, Plus, Pencil, Trash2, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import MetaTemplateFormDialog from './MetaTemplateFormDialog';
import HintTooltip from '../common/HintTooltip';

interface MetaTemplateManagerProps {
  instanciaId: string;
  open: boolean;
  onClose: () => void;
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  APPROVED: { color: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircle className="h-3.5 w-3.5 text-green-500" /> },
  REJECTED: { color: 'bg-red-50 text-red-700 border-red-200', icon: <XCircle className="h-3.5 w-3.5 text-red-500" /> },
  PENDING: { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: <Clock className="h-3.5 w-3.5 text-yellow-500" /> },
  DISABLED: { color: 'bg-gray-50 text-gray-500 border-gray-200', icon: <XCircle className="h-3.5 w-3.5 text-gray-400" /> },
  IN_DEVELOP: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: <Clock className="h-3.5 w-3.5 text-blue-500" /> },
};

const MetaTemplateManager: React.FC<MetaTemplateManagerProps> = ({ instanciaId, open, onClose }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [templates, setTemplates] = useState<MetaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MetaTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<MetaTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTemplates = useCallback(async (after?: string, append = false) => {
    if (!after) setLoading(true);
    else setLoadingMore(true);
    try {
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      if (search) params.name = search;
      if (after) params.after = after;
      const res = await instanciaService.listarTemplates(instanciaId, params);
      const newData = res.data.data || [];
      setTemplates(prev => append ? [...prev, ...newData] : newData);
      setNextPage(res.data.paging?.next || null);
    } catch (err: any) {
      console.error('Erro ao listar templates:', err);
      toast({
        title: t('templates.fetchErrorTitle'),
        description: err.response?.data?.message || t('templates.fetchErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [instanciaId, filterStatus, search, toast, t]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open, fetchTemplates]);

  const handleDelete = async (template: MetaTemplate) => {
    setIsDeleting(true);
    try {
      await instanciaService.excluirTemplate(instanciaId, [{ name: template.name, language: template.language }]);
      toast({ title: t('templates.deletedTitle'), description: t('templates.deletedDesc', { name: template.name }) });
      setDeletingTemplate(null);
      fetchTemplates();
    } catch (err: any) {
      console.error('Erro ao excluir template:', err);
      toast({
        title: t('templates.deleteErrorTitle'),
        description: err.response?.data?.message || t('templates.deleteErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getBodyPreview = (template: MetaTemplate): string => {
    if (!template.components) return '';
    const body = template.components.find(c => c.type === 'BODY');
    if (!body?.text) return '';
    return body.text.length > 100 ? body.text.substring(0, 100) + '...' : body.text;
  };

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.language.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col dark:bg-gray-800">
          <DialogHeader>
            <DialogTitle className="dark:text-white">{t('templates.title')}</DialogTitle>
            <DialogDescription className="dark:text-gray-400">
              {t('templates.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('templates.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              <Button
                variant={filterStatus === '' ? 'default' : 'outline'}
                size="sm"
                className="h-8 text-xs"
                onClick={() => setFilterStatus('')}
              >
                {t('templates.all')}
              </Button>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <Button
                  key={key}
                  variant={filterStatus === key ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setFilterStatus(key)}
                >
                  {t(`templates.status.${key}`)}
                </Button>
              ))}
            </div>
            <HintTooltip text={t('templates.hints.newTemplate')}>
              <Button
                size="sm"
                className="h-8"
                onClick={() => { setEditingTemplate(null); setFormOpen(true); }}
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('templates.newTemplate')}
              </Button>
            </HintTooltip>
          </div>

          <div className="flex-1 overflow-y-auto mt-2">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                {t('templates.loadingTemplates')}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                {templates.length === 0
                  ? t('templates.noTemplatesFound')
                  : t('templates.noTemplatesMatch')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((template) => {
                  const statusCfg = STATUS_CONFIG[template.status] || STATUS_CONFIG.PENDING;
                  return (
                    <div
                      key={template.id}
                      className="border rounded-lg p-4 bg-white dark:bg-gray-700 flex flex-col gap-2 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-sm dark:text-white truncate">{template.name}</h4>
                        <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border shrink-0 ${statusCfg.color}`}>
                          {statusCfg.icon}
                          {t(`templates.status.${template.status}`, template.status)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{template.language}</span>
                        <span>·</span>
                        <span>{t(`templates.category.${template.category}`, template.category)}</span>
                      </div>
                      {getBodyPreview(template) && (
                        <p className="text-xs text-muted-foreground line-clamp-2 italic">
                          {getBodyPreview(template)}
                        </p>
                      )}
                      <div className="flex items-center gap-1 mt-auto pt-2">
                        <HintTooltip text={t('templates.hints.edit')}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => { setEditingTemplate(template); setFormOpen(true); }}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            {t('common.edit')}
                          </Button>
                        </HintTooltip>
                        <HintTooltip text={t('templates.hints.delete')}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeletingTemplate(template)}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            {t('common.delete')}
                          </Button>
                        </HintTooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {nextPage && !loading && (
              <div className="flex justify-center mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTemplates(nextPage, true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  {t('templates.loadMore')}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {formOpen && (
        <MetaTemplateFormDialog
          instanciaId={instanciaId}
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditingTemplate(null); }}
          template={editingTemplate}
          onSaved={() => { setFormOpen(false); setEditingTemplate(null); fetchTemplates(); }}
        />
      )}

      <Dialog open={!!deletingTemplate} onOpenChange={(v) => !v && setDeletingTemplate(null)}>
        <DialogContent className="sm:max-w-md dark:bg-gray-800">
          <DialogHeader>
            <DialogTitle className="dark:text-white">{t('templates.confirmDeleteTitle')}</DialogTitle>
            <DialogDescription className="dark:text-gray-400">
              {t('templates.confirmDeleteDescPrefix')} <strong>{deletingTemplate?.name}</strong>{t('templates.confirmDeleteDescSuffix')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeletingTemplate(null)} disabled={isDeleting}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={() => deletingTemplate && handleDelete(deletingTemplate)} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('common.delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MetaTemplateManager;
